// Sales Return module — bill-first "+Add" create flow.
//
// Step 1: search + pick a bill (SalesReturnBillPicker). Step 2: show that
// bill's full detail (styled after orders/ViewBillingSheet's header) and
// every item billed on it, with a per-item Normal/Damage return form. Each
// submit reuses the existing single-item createOrUpdateSalesReturn mutation
// (via the parent's onSubmitReturn), one item at a time — the parent then
// refetches this bill's details so quantityLeft stays accurate and more
// items on the same bill can be returned next.
import * as React from "react";
import { ChevronLeft } from "lucide-react";
import moment from "moment";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { Button } from "ui/button";
import { Input } from "ui/input";
import { RadioGroup, RadioGroupItem } from "ui/radio-group";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "ui/table";
import { FieldRow } from "../../shared/form-section";
import { cn } from "lib/utils";
import AppConstant from "../../Globals/AppConstants";
import SalesReturnBillPicker from "./SalesReturnBillPicker";

// Product Id / Action stay pinned while MRP/Selling Price/Discount/Qty/Qty
// Left scroll horizontally underneath — same sticky-column convention as
// shared/data-table.tsx's `meta: { sticky }`, hand-rolled here since this
// table has an inline-expanding per-row return form DataTable doesn't support.
const STICKY_LEFT_HEADER = "sticky left-0 z-20 border-r bg-muted";
const STICKY_LEFT_CELL = "sticky left-0 z-10 border-r bg-background";
const STICKY_RIGHT_HEADER = "sticky right-0 z-20 border-l bg-muted";
const STICKY_RIGHT_CELL = "sticky right-0 z-10 border-l bg-background";

const RETURN_TYPES: Record<string, { label: string; statusRefId: number; status: string }> = {
  NORMAL: { label: "Normal Return", statusRefId: 2, status: AppConstant.inStock },
  DAMAGE: { label: "Damage Return", statusRefId: 5, status: AppConstant.damaged },
};

export interface SalesReturnBillFormSheetProps {
  open: boolean;
  onClose: () => void;
  billSearchText: string;
  onBillSearchChange: (value: string) => void;
  billSearchResults: any[];
  billSearchLoading: boolean;
  selectedBill: any;
  onSelectBill: (bill: any) => void;
  onBackToSearch: () => void;
  billDetails: any;
  billDetailsLoading: boolean;
  submitting: boolean;
  onSubmitReturn: (item: any, payload: { statusRefId: number; status: string; qty: number; returnedAmount: number }) => void;
}

export function SalesReturnBillFormSheet({
  open,
  onClose,
  billSearchText,
  onBillSearchChange,
  billSearchResults,
  billSearchLoading,
  selectedBill,
  onSelectBill,
  onBackToSearch,
  billDetails,
  billDetailsLoading,
  submitting,
  onSubmitReturn,
}: SalesReturnBillFormSheetProps) {
  const [activeProductId, setActiveProductId] = React.useState<string | null>(null);
  const [returnType, setReturnType] = React.useState<"NORMAL" | "DAMAGE">("NORMAL");
  const [qty, setQty] = React.useState<number>(1);
  const [returnedAmount, setReturnedAmount] = React.useState<number>(0);
  const [error, setError] = React.useState<string | undefined>();
  const [itemSearchText, setItemSearchText] = React.useState("");

  const bill = billDetails?.bill;
  const items = billDetails?.items || [];
  const filteredItems = React.useMemo(() => {
    const search = itemSearchText.trim().toLowerCase();
    if (!search) return items;
    return items.filter((item: any) => String(item?.productId ?? "").toLowerCase().includes(search));
  }, [items, itemSearchText]);

  // Return quantity can never exceed what is still returnable on this bill
  // line (quantityLeft = billed quantity minus what earlier returns already
  // took). Returns undefined when `value` is acceptable. An unreadable
  // quantityLeft is treated as "nothing returnable" rather than passing
  // silently, so a bad/missing figure can't let an over-return through.
  const validateQty = (item: any, value: number): string | undefined => {
    const max = Number(item?.quantityLeft);
    if (!Number.isFinite(max) || max <= 0) return "No returnable quantity left on this item.";
    if (!value || value < 1) return "Quantity is required and must be at least 1.";
    if (!Number.isInteger(value)) return "Quantity must be a whole number.";
    if (value > max) {
      return `Return quantity cannot exceed the quantity bought — only ${max} of ${item?.quantity} left to return.`;
    }
    return undefined;
  };

  const openItemForm = (item: any) => {
    const defaultQty = item?.quantityLeft > 0 ? 1 : 0;
    const defaultAmount = Math.round(defaultQty * item?.mrp * (1 - (item?.discountPercentage || 0) / 100) * 100) / 100;
    setActiveProductId(item?.productId);
    setReturnType("NORMAL");
    setQty(defaultQty);
    setReturnedAmount(defaultAmount);
    setError(undefined);
  };

  const closeItemForm = () => {
    setActiveProductId(null);
    setError(undefined);
  };

  // Collapse the open row once the bill's items are refetched after a
  // successful submit — quantityLeft has changed underneath it.
  const itemsRef = React.useRef(items);
  React.useEffect(() => {
    if (itemsRef.current !== items) {
      itemsRef.current = items;
      closeItemForm();
    }
  }, [items]);

  // Clear the item search whenever a different bill is opened.
  React.useEffect(() => {
    setItemSearchText("");
  }, [bill?.invoice]);

  const handleQtyChange = (item: any, value: string) => {
    const next = Number(value) || 0;
    setQty(next);
    const amount = Math.round(next * item?.mrp * (1 - (item?.discountPercentage || 0) / 100) * 100) / 100;
    setReturnedAmount(amount);
    // Validate as they type so the cap is visible before Save is reached.
    setError(validateQty(item, next));
  };

  const handleSubmit = (item: any) => {
    const qtyError = validateQty(item, qty);
    if (qtyError) {
      setError(qtyError);
      return;
    }
    const amountLimit = qty * item?.mrp;
    if (returnedAmount == null || returnedAmount > amountLimit) {
      setError(`Returned amount must be less than or equal to ₹ ${amountLimit}`);
      return;
    }
    const type = RETURN_TYPES[returnType];
    onSubmitReturn(item, {
      statusRefId: type.statusRefId,
      status: type.status,
      qty,
      returnedAmount,
    });
  };

  const handleClose = () => {
    closeItemForm();
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
        <SheetHeader className="border-b px-5 py-4">
          <SheetTitle>Sales Return</SheetTitle>
        </SheetHeader>

        {!selectedBill && (
          <SalesReturnBillPicker
            searchText={billSearchText}
            onSearchChange={onBillSearchChange}
            results={billSearchResults}
            loading={billSearchLoading}
            onSelect={onSelectBill}
          />
        )}

        {selectedBill && (
          <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-5">
            <button
              type="button"
              onClick={onBackToSearch}
              className="inline-flex w-fit items-center gap-1 text-sm text-primary hover:underline"
            >
              <ChevronLeft className="h-4 w-4" /> Search another bill
            </button>

            {billDetailsLoading && !bill && (
              <div className="text-sm text-muted-foreground">Loading bill details...</div>
            )}

            {bill && (
              <>
                <div className="grid shrink-0 grid-cols-2 gap-4 rounded-lg border p-4 text-sm">
                  <div className="space-y-1">
                    <div className="font-medium text-foreground">Bill No - {bill?.invoice}</div>
                    <div className="text-muted-foreground">
                      Date - {bill?.date ? moment(bill.date).format("DD-MMM-YYYY") : "-"}
                    </div>
                    <div className="text-muted-foreground">Customer Name - {bill?.customerName || "-"}</div>
                    <div className="text-muted-foreground">Mobile Number - {bill?.mobileNumberList || "Nil"}</div>
                    <div className="text-muted-foreground">Payment Method - {bill?.paymentMethod || "-"}</div>
                    <div className="text-muted-foreground">Employee - {bill?.billedEmployeeName || "-"}</div>
                    <div className="text-muted-foreground">Item Count - {bill?.itemsCount ?? "-"}</div>
                  </div>
                  <div className="space-y-1.5 border-l pl-4">
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="tabular-nums">₹ {bill?.subtotal}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Line discounts</span>
                      <span className="tabular-nums">− ₹ {bill?.lineDiscounts}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">Overall discount</span>
                      <span className="tabular-nums">− ₹ {bill?.overallDiscount}</span>
                    </div>
                    <div className="my-1 border-t" />
                    <div className="flex items-center justify-between font-medium text-foreground">
                      <span>Grand Total</span>
                      <span className="tabular-nums">₹ {bill?.totalamount}</span>
                    </div>
                  </div>
                </div>

                <Input
                  placeholder="Search Product Id"
                  value={itemSearchText}
                  onChange={(e) => setItemSearchText(e.target.value)}
                  className="shrink-0"
                />

                <div className="rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className={STICKY_LEFT_HEADER}>Product Id</TableHead>
                        <TableHead className="whitespace-nowrap">MRP</TableHead>
                        <TableHead className="whitespace-nowrap">Selling Price</TableHead>
                        <TableHead className="whitespace-nowrap">Discount</TableHead>
                        <TableHead className="whitespace-nowrap">Qty</TableHead>
                        <TableHead className="whitespace-nowrap">Qty Left</TableHead>
                        <TableHead className={cn(STICKY_RIGHT_HEADER, "text-right")}>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredItems.length === 0 && (
                        <TableRow className="hover:bg-transparent">
                          <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                            No items match "{itemSearchText}".
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredItems.map((item: any, index: number) => {
                        const discountPercentage = Number(item?.discountPercentage) || 0;
                        const sellingPrice = Math.round(Number(item?.mrp) * (1 - discountPercentage / 100) * 100) / 100;
                        const discountAmount = Math.round((Number(item?.mrp) - sellingPrice) * 100) / 100;
                        const isActive = activeProductId === item?.productId;
                        // Live cap check for the open row, so the Qty field shows the
                        // limit and Save stays blocked while it is exceeded.
                        const liveQtyError = isActive ? validateQty(item, qty) : undefined;
                        return (
                          <React.Fragment key={item?.productId ?? index}>
                            <TableRow>
                              <TableCell className={cn(STICKY_LEFT_CELL, "min-w-0 break-words")}>{item?.productId}</TableCell>
                              <TableCell className="tabular-nums">₹ {item?.mrp}</TableCell>
                              <TableCell className="tabular-nums">₹ {sellingPrice}</TableCell>
                              <TableCell className="whitespace-nowrap tabular-nums">
                                {discountPercentage > 0 ? `${discountPercentage}% (₹ ${discountAmount})` : "-"}
                              </TableCell>
                              <TableCell className="tabular-nums">{item?.quantity}</TableCell>
                              <TableCell className="tabular-nums">{item?.quantityLeft}</TableCell>
                              <TableCell className={cn(STICKY_RIGHT_CELL, "text-right")}>
                                {item?.quantityLeft > 0 ? (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant={isActive ? "secondary" : "outline"}
                                    onClick={() => (isActive ? closeItemForm() : openItemForm(item))}
                                  >
                                    Return
                                  </Button>
                                ) : (
                                  <span className="whitespace-nowrap text-xs text-muted-foreground">Fully Returned</span>
                                )}
                              </TableCell>
                            </TableRow>
                            {isActive && (
                              <TableRow className="hover:bg-transparent">
                                <TableCell colSpan={7} className="bg-muted/30 p-0">
                                  <div className="space-y-3 border-t px-3 py-3">
                                    <FieldRow label="Return Type" required>
                                      <RadioGroup
                                        className="flex flex-wrap gap-4"
                                        value={returnType}
                                        onValueChange={(v) => setReturnType(v as "NORMAL" | "DAMAGE")}
                                      >
                                        {Object.entries(RETURN_TYPES).map(([key, opt]) => (
                                          <label key={key} className="flex items-center gap-1.5 text-sm">
                                            <RadioGroupItem value={key} /> {opt.label}
                                          </label>
                                        ))}
                                      </RadioGroup>
                                    </FieldRow>
                                    <div className="grid grid-cols-2 gap-3">
                                      {item?.isMultipleRecords == 0 ? (
                                        <FieldRow
                                          label="Qty"
                                          required
                                          error={liveQtyError}
                                          hint={`Max ${item?.quantityLeft} of ${item?.quantity} returnable`}
                                        >
                                          <Input
                                            type="number"
                                            min={1}
                                            max={item?.quantityLeft}
                                            value={qty}
                                            onChange={(e) => handleQtyChange(item, e.target.value)}
                                          />
                                        </FieldRow>
                                      ) : (
                                        <FieldRow label="Qty" error={liveQtyError}>
                                          <Input value={qty} disabled readOnly />
                                        </FieldRow>
                                      )}
                                      <FieldRow label="Returned Amount" required>
                                        <Input
                                          type="number"
                                          min={0}
                                          value={returnedAmount}
                                          onChange={(e) => { setReturnedAmount(Number(e.target.value) || 0); setError(undefined); }}
                                        />
                                      </FieldRow>
                                    </div>
                                    {error && error !== liveQtyError && (
                                      <div className="text-sm text-destructive">{error}</div>
                                    )}
                                    <div className="flex justify-end gap-2">
                                      <Button type="button" variant="outline" size="sm" onClick={closeItemForm}>
                                        {AppConstant?.cancel}
                                      </Button>
                                      <Button type="button" size="sm" disabled={submitting || !!liveQtyError} onClick={() => handleSubmit(item)}>
                                        {AppConstant?.save}
                                      </Button>
                                    </div>
                                  </div>
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export default SalesReturnBillFormSheet;
